/**
 * Campaigns hub — Project Harmony · Patch 34.2.1d
 *
 * One continuous surface. Lists blueprints, links into the existing
 * per-campaign flow (Commander → Strategy → Room → Editorial).
 *
 * Patch 34.2.1d · Every card now displays the CampaignRun-derived
 * stage and health from `GET /workspaces/{ws}/campaign-runs`. If a
 * blueprint has no matching CampaignRun projection we render
 * "Campaign state unavailable" rather than silently inventing a
 * healthy status. Lifecycle status is NEVER derived from blueprint
 * status alone.
 */
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive as ArchiveIcon, ChevronRight, Plus, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { archiveBlueprint, restoreBlueprint } from "@/features/campaign-commander/api";
import { PageHeader } from "@/shared/ui/page-header";
import { useCurrentWorkspace } from "@/entities/workspace";
import { ApiError, http } from "@/shared/api/http";
import {
  useCampaignRuns,
  type CampaignRun,
  type RunHealth,
  type RunStage,
} from "@/entities/campaign-run";
import { opsSummary, type OpsSummary } from "@/entities/ops/api";
import { missionKeys } from "@/entities/missions/hooks";
import { HarmonyEmpty } from "@/shared/ui/harmony-empty";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { useState } from "react";

interface BlueprintRow {
  id: string;
  status: string;
  operating_intent?: string | null;
  updated_at?: string | null;
  answers?: { promotion?: string; business_goal?: string };
}

interface BlueprintCounts {
  active: number;
  archived: number;
}

interface BlueprintsListResponse {
  blueprints: BlueprintRow[];
  counts?: BlueprintCounts;
}

async function listBlueprints(archived: "excluded" | "only"): Promise<BlueprintsListResponse> {
  // Patch 34.2.1e · No silent try/catch — a failed blueprint list must
  // NEVER masquerade as "zero blueprints" and drop the founder into
  // "Create your first campaign".
  // Patch 37.4.39 · archived filter selects the "Active" or
  // "Archived" tab. Backend counts render the tab badges without a
  // second round-trip.
  return http<BlueprintsListResponse>(
    `/orchestration/blueprints?archived=${archived}`,
  );
}

/** Patch 34.2.1d · Canonical stage → display label. */
const STAGE_LABEL: Record<RunStage, string> = {
  blueprint: "Blueprint",
  pack: "Generating",
  generating: "Generating",
  reconciling: "Reconciling",
  review: "Needs Review",
  approved: "Approved",
  scheduled: "Scheduled",
  published: "Published",
  archived: "Archived",
};

/**
 * Health → display label + tone. Health governs the badge; stage is
 * folded in only when the campaign is healthy so we don't hide a
 * blocker under a lifecycle label.
 */
const HEALTH_LABEL: Record<RunHealth, string> = {
  healthy: "Healthy",
  generating: "Generating",
  promotion_incomplete: "Reconciling",
  reconciliation_failed: "Reconciliation Failed",
  partial: "Partial",
  failed: "Failed",
  blocked: "Blocked",
};

const HEALTH_TONE: Record<RunHealth, string> = {
  healthy: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  generating: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  promotion_incomplete: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  reconciliation_failed: "bg-red-500/10 text-red-400 border-red-500/30",
  partial: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
  blocked: "bg-red-500/10 text-red-400 border-red-500/30",
};

const STAGE_UNAVAILABLE_TONE = "bg-[var(--md-ink-0)]/40 text-[var(--md-fog)] border-[color:var(--md-line)]";

/**
 * Determine what to show on the badge. If the campaign is healthy we
 * show the lifecycle stage; otherwise we show the health label so the
 * failure mode is impossible to miss.
 */
function badgeFor(run: CampaignRun): { label: string; tone: string } {
  if (run.health === "healthy") {
    return { label: STAGE_LABEL[run.stage] ?? run.stage, tone: HEALTH_TONE.healthy };
  }
  return { label: HEALTH_LABEL[run.health] ?? run.health, tone: HEALTH_TONE[run.health] };
}

export default function CampaignsHubPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("filter");
  // Patch 37.4.39 · Active / Archived tabs. Default = Active.
  const archivedTab = sp.get("archived") === "only";
  const archivedQuery = archivedTab ? "only" : "excluded";
  const ws = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const q = useQuery({
    // Patch 34.2.1d · workspace-scoped list so switching workspaces
    // does not serve stale blueprints from a prior tenant.
    // Patch 37.4.39 · include the archived-tab flag in the cache key
    // so switching tabs never serves stale rows.
    queryKey: ["campaigns", "hub", ws?.id, archivedQuery],
    queryFn: () => listBlueprints(archivedQuery),
    enabled: Boolean(ws?.id),
    refetchInterval: 30_000,
  });

  const counts = q.data?.counts;
  const activeBadgeCount = counts?.active ?? 0;
  const archivedBadgeCount = counts?.archived ?? 0;

  // Patch 34.2.1d · Canonical CampaignRun projection — one query per
  // workspace, indexed by blueprint_id for O(1) lookups per row.
  const runs = useCampaignRuns(ws?.id ?? null, 200);
  const runsByBlueprint = new Map<string, CampaignRun>();
  for (const r of runs.data ?? []) {
    if (r.blueprint_id) runsByBlueprint.set(r.blueprint_id, r);
  }

  // Live Content Review counter — no ghost text, alive by design.
  const ops = useQuery({
    queryKey: ["ops-summary", ws?.id],
    queryFn: () => opsSummary(ws!.id),
    enabled: Boolean(ws?.id),
    refetchInterval: 30_000,
    retry: false,
  });
  const awaiting = (ops.data as OpsSummary | undefined)?.counters?.find(
    (c) => c.status === "needs_review",
  )?.count ?? 0;
  const drafts = (ops.data as OpsSummary | undefined)?.counters?.find(
    (c) => c.status === "draft",
  )?.count ?? 0;
  const reviewCount = awaiting + drafts;

  // Patch 37.4.39 · Archive / Restore mutations. Both refresh the two
  // tab caches so the counts + row lists converge immediately.
  const invalidateHub = () => {
    queryClient.invalidateQueries({ queryKey: ["campaigns", "hub", ws?.id, "excluded"] });
    queryClient.invalidateQueries({ queryKey: ["campaigns", "hub", ws?.id, "only"] });
    // Patch 37.4.39 · Focus Flow resume tile reads the mission list via
    // useResumableMissions → missionKeys.list(workspaceId). Invalidating
    // that (and the broader missions namespace as a defensive fallback)
    // guarantees the tile hides on archive and reappears on restore
    // without waiting for the 30 s staleTime.
    if (ws?.id) {
      queryClient.invalidateQueries({ queryKey: missionKeys.list(ws.id) });
    }
    queryClient.invalidateQueries({ queryKey: missionKeys.all() });
  };
  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveBlueprint(id),
    onSuccess: () => {
      toast.success("Campaign archived. Generated assets are preserved.");
      invalidateHub();
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Couldn't archive this campaign.";
      toast.error(msg);
    },
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreBlueprint(id),
    onSuccess: () => {
      toast.success("Campaign restored.");
      invalidateHub();
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Couldn't restore this campaign.";
      toast.error(msg);
    },
  });

  const [archiveTarget, setArchiveTarget] = useState<BlueprintRow | null>(null);

  const all = q.data?.blueprints ?? [];
  const rows = filter === "awaiting-approval"
    ? all.filter((b) => ["planned", "approved"].includes(b.status))
    : all;

  const setTab = (nextArchivedOnly: boolean) => {
    const next = new URLSearchParams(sp);
    if (nextArchivedOnly) next.set("archived", "only");
    else next.delete("archived");
    next.delete("filter");     // exit "awaiting-approval" sub-view
    setSp(next);
  };

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-8 md:px-8 md:py-12" data-testid="campaigns-hub">
      <PageHeader
        eyebrow={`${ws?.slug ?? "workspace"} · Campaigns`}
        title="Campaigns"
        subtitle="Tell us the business outcome you want. Your Marketing Department will build the plan and keep the campaign moving."
        action={
          <Button asChild data-testid="campaigns-hub-new">
            <Link to="/campaigns/new">
              <Plus className="h-4 w-4 mr-1.5" aria-hidden /> New Campaign
            </Link>
          </Button>
        }
      />

      {/* Content Review is a primary founder action whenever inventory waits. */}
      <Link
        to="/campaigns/review"
        data-testid="campaigns-review-shortcut"
        className={`mb-6 flex flex-col items-stretch gap-3 rounded-xl border px-4 py-4 shadow-sm transition sm:flex-row sm:items-center
          ${reviewCount > 0
            ? "border-[var(--md-amber)] bg-[var(--md-amber)]/15 shadow-[0_0_28px_rgba(229,180,84,0.12)] hover:bg-[var(--md-amber)]/20"
            : "border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 hover:border-[var(--md-amber)]/40 hover:bg-[var(--md-ink-1)]"}`}
      >
        <span
          aria-hidden
          className={`grid h-9 w-9 place-items-center rounded-full ${
            reviewCount > 0 ? "bg-[var(--md-amber)]/20 text-[var(--md-amber)]" : "bg-emerald-500/10 text-emerald-500"
          }`}
        >
          {reviewCount > 0 ? "!" : "✓"}
        </span>
        <div className="flex-1">
          <p className="text-sm font-medium text-[var(--md-ivory-0)]" data-testid="campaigns-review-headline">
            Content Review
          </p>
          <p className="text-xs text-[var(--md-fog)]" data-testid="campaigns-review-count">
            {ops.isLoading
              ? "Loading…"
              : reviewCount === 0
                ? "You're caught up. Every asset has been approved."
                : `${reviewCount} asset${reviewCount === 1 ? "" : "s"} waiting for your approval.`}
          </p>
        </div>
        <span
          className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold ${
            reviewCount > 0
              ? "bg-[var(--md-amber)] text-[var(--md-ink-0)]"
              : "border border-[color:var(--md-line)] text-[var(--md-fog)]"
          }`}
          data-testid="campaigns-review-cta"
        >
          Open Review Queue <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      </Link>

      {/* Patch 37.4.39 · Active / Archived tabs. Counts render from
          the backend so the badge and the "you're at your limit"
          gate can never disagree. */}
      <div
        className="mb-4 flex items-center gap-2 border-b border-[color:var(--md-line)] pb-1"
        role="tablist"
        aria-label="Campaign lifecycle filter"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!archivedTab}
          data-testid="campaigns-tab-active"
          onClick={() => setTab(false)}
          className={cn(
            "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition",
            !archivedTab
              ? "border-[var(--md-amber)] text-[var(--md-ivory-0)]"
              : "border-transparent text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]",
          )}
        >
          Active <span className="ml-1.5 text-xs text-[var(--md-fog)]" data-testid="campaigns-tab-active-count">({activeBadgeCount})</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={archivedTab}
          data-testid="campaigns-tab-archived"
          onClick={() => setTab(true)}
          className={cn(
            "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition",
            archivedTab
              ? "border-[var(--md-amber)] text-[var(--md-ivory-0)]"
              : "border-transparent text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]",
          )}
        >
          Archived <span className="ml-1.5 text-xs text-[var(--md-fog)]" data-testid="campaigns-tab-archived-count">({archivedBadgeCount})</span>
        </button>
      </div>

      {filter === "awaiting-approval" ? (
        <p className="mb-4 text-xs text-[var(--md-fog)]">
          Showing campaigns that need your approval.{" "}
          <Link to="/campaigns" className="underline">See all</Link>
        </p>
      ) : null}

      {q.isLoading ? (
        <Card className="p-6 text-sm text-[var(--md-fog)]">Loading…</Card>
      ) : q.isError ? (
        /* Patch 34.2.1e · Blueprint API failed. NEVER show the empty
           state (which would falsely suggest "no campaigns exist"). */
        <Card
          data-testid="campaigns-hub-error"
          className="border-red-500/40 bg-red-500/10"
        >
          <div className="p-6 text-sm">
            <div
              className="font-semibold text-red-800"
              data-testid="campaigns-hub-error-title"
            >
              We couldn&apos;t load your campaigns.
            </div>
            <p className="mt-1 text-xs text-red-900/80">
              The blueprints API is unreachable. Your campaigns are not
              gone — try again. Do NOT create a new campaign here if
              you know one already exists on this workspace.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => void q.refetch()}
                data-testid="campaigns-hub-error-retry"
              >
                Retry
              </Button>
              <Button
                asChild
                size="sm"
                variant="ghost"
                data-testid="campaigns-hub-error-report"
              >
                <a href="mailto:support@orkexra.com?subject=Campaigns%20hub%20unreachable">
                  Report Issue
                </a>
              </Button>
            </div>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <HarmonyEmpty
          eyebrow="Your Marketing Department is ready."
          title="Create your first campaign and tell us what you want to accomplish."
          body="Answer six questions about the promotion. Orkexra drafts the strategy, generates the assets, and lays out the calendar. You approve."
          icon={<Sparkles className="h-5 w-5" aria-hidden />}
          primary={{ label: "Create Campaign", to: "/campaigns/new", testId: "campaigns-empty-cta" }}
          testId="campaigns-hub-empty"
        />
      ) : (
        <ul className="space-y-2" data-testid="campaigns-hub-list">
          {rows.map((bp) => {
            const run = runsByBlueprint.get(bp.id);
            // Patch 34.2.2d · When there is no run for this Blueprint,
            // classify the reason using the Blueprint's own status —
            // never fall through to a generic "State unavailable"
            // (the exact contradiction the executive flagged when
            // Campaign Pulse showed approved assets while cards said
            // the projection was empty).
            const unavailable = !run;
            const legacyLabel = ((): { label: string; detail: string } => {
              if (bp.status === "archived") {
                // Patch 37.4.39-fix (iter 71) · surface an explicit
                // ``Archived`` status pill on archived rows instead of
                // reusing the ambiguous "Not generated yet" label. The
                // sub-line still explains *why* the row is inert.
                return {
                  label: "Archived",
                  detail: "This campaign was archived before generation started.",
                };
              }
              if (["draft", "planned"].includes(bp.status)) {
                return {
                  label: "Not generated yet",
                  detail: "Approve the Marketing Strategy to start generation.",
                };
              }
              if (["executing"].includes(bp.status)) {
                return {
                  label: "Generating",
                  detail: "Generation is in flight — check back shortly.",
                };
              }
              return {
                label: "Campaign state requires migration",
                detail:
                  "This Blueprint has no linked Campaign Pack. Ask an admin " +
                  "to run the legacy migration to reconnect it.",
              };
            })();
            const isArchived = bp.status === "archived";
            // Patch 37.4.39-fix (iter 72) · when the blueprint is
            // archived, ALWAYS surface an explicit "Archived" badge —
            // never the run-derived stage badge — even if a campaign
            // pack was generated before the founder archived. The
            // status pill is the truth of what the row is now; the
            // run's stage is history.
            const badge = isArchived
              ? { label: "Archived", tone: STAGE_UNAVAILABLE_TONE }
              : run
                ? badgeFor(run)
                : { label: legacyLabel.label, tone: STAGE_UNAVAILABLE_TONE };
            const canArchive = ["draft", "planned", "paused"].includes(bp.status);
            return (
              <li key={bp.id} className="relative">
                <Link
                  to={`/campaigns/${bp.id}/room`}
                  data-testid={`campaigns-hub-row-${bp.id}`}
                  className="flex flex-col gap-2 rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 px-4 py-3 pr-20 transition hover:border-[var(--md-amber)]/50 hover:bg-[var(--md-ink-1)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge
                          variant="outline"
                          className={badge.tone}
                          data-testid={`campaigns-hub-row-${bp.id}-badge`}
                        >
                          {badge.label}
                        </Badge>
                        <span
                          className="text-sm font-medium text-[var(--md-ivory-0)]"
                          data-testid={`campaigns-hub-row-${bp.id}-title`}
                        >
                          {bp.answers?.promotion ?? `Campaign ${bp.id.slice(0, 8)}`}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--md-fog)]">
                        {bp.answers?.business_goal ?? "—"}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
                  </div>

                  {unavailable ? (
                    <div
                      data-testid={`campaigns-hub-row-${bp.id}-unavailable`}
                      className="text-[11px] text-[var(--md-fog)]"
                    >
                      {legacyLabel.detail}
                    </div>
                  ) : (
                    <div
                      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[var(--md-fog)]"
                      data-testid={`campaigns-hub-row-${bp.id}-counters`}
                    >
                      <span data-testid={`campaigns-hub-row-${bp.id}-drafts`}>
                        Drafts <strong>{run.counters.draft}</strong>
                      </span>
                      <span data-testid={`campaigns-hub-row-${bp.id}-approved`}>
                        Approved <strong>{run.counters.approved}</strong>
                      </span>
                      <span data-testid={`campaigns-hub-row-${bp.id}-scheduled`}>
                        Scheduled <strong>{run.counters.scheduled}</strong>
                      </span>
                      <span data-testid={`campaigns-hub-row-${bp.id}-published`}>
                        Published <strong>{run.counters.published}</strong>
                      </span>
                      {run.blocker && (
                        <span
                          data-testid={`campaigns-hub-row-${bp.id}-blocker`}
                          className="text-red-400"
                        >
                          Blocker: {run.blocker}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
                {/* Patch 37.4.39 · per-row Archive / Restore action.
                    Absolutely positioned so the Link above is unaffected. */}
                {canArchive ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="absolute right-3 top-3 h-8 gap-1.5"
                    data-testid={`campaigns-hub-row-${bp.id}-archive`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setArchiveTarget(bp);
                    }}
                  >
                    <ArchiveIcon className="h-3.5 w-3.5" aria-hidden />
                    <span className="sr-only sm:not-sr-only">Archive</span>
                  </Button>
                ) : null}
                {isArchived ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={restoreMutation.isPending}
                    className="absolute right-3 top-3 h-8 gap-1.5"
                    data-testid={`campaigns-hub-row-${bp.id}-restore`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      restoreMutation.mutate(bp.id);
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    <span className="sr-only sm:not-sr-only">Restore</span>
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* Patch 37.4.39 · confirmation modal — shows the campaign name
          and reassures the founder that generated assets are preserved. */}
      <AlertDialog
        open={archiveTarget != null}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
      >
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-[var(--md-ivory-0)]" data-testid="archive-confirm-campaign-name">
                {archiveTarget?.answers?.promotion ?? archiveTarget?.id}
              </span>
              <span className="block mt-2">
                Generated assets are preserved. You can restore this
                campaign from the Archived tab later. Archiving frees up
                a slot toward your active campaign limit.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="archive-confirm-submit"
              disabled={archiveMutation.isPending}
              onClick={() => {
                if (archiveTarget) archiveMutation.mutate(archiveTarget.id);
                setArchiveTarget(null);
              }}
            >
              Archive campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
